import React, { useEffect, useState, useMemo } from 'react';
import classNames from 'classnames';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { Claims } from './NestedContainers/Claims';
import { Orders } from './NestedContainers/Orders';
import { Settings } from './NestedContainers/Settings';
import { OrderSettings } from './NestedContainers/OrderEditing/OrderSettings';
import { NotFound } from 'containers/NotFound';
import { SideBar } from 'components/Sidebar';
import { MenuIcon } from '@heroicons/react/outline';
import { NewClaim } from './NestedContainers/NewClaim';
import { useStore } from 'context/store-context';
import { SingleClaim } from './NestedContainers/Claim';
import { OnboardingWizard } from 'components/OnboardingWizard';
import { useMutation } from '@apollo/client';
import { withNotification } from 'components/Notification';
import { UPDATE_STORE } from 'gql/mutations';
import { NotificationTypes, apiErrorHandler } from 'utils';
import { AnnouncementHeader } from 'components/AnnouncementHeader';
import { Button } from 'components/Button';
import { SizeEnum } from 'utils';
import { useUser } from 'context/user-context';
import { userRoles } from 'types/user';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { UpdateAppInstallModal } from 'components/UpdateAppInstallModal';
import { ReferralProgram } from './NestedContainers/ReferralProgram';
import { ViewOrder } from './NestedContainers/Order';
import { Home } from './NestedContainers/Home';
import AppBar from 'components/AppBar';
import { StoreSelectorWrapper } from 'components/SelectStoreModal';
import { Returns } from './NestedContainers/Returns';
import { ReturnDetail } from './NestedContainers/ReturnDetail';
import { TrackingDetail } from './NestedContainers/TrackingDetail';
import { MerchantRequests } from './NestedContainers/MerchantRequests';
import { ReturnInspectionQueue } from './NestedContainers/ReturnInspectionQueue';
import { RequestsWorkspace } from './NestedContainers/RequestsWorkspace';
import { RequestReports } from './NestedContainers/RequestReports';
import { canManageRequests, MerchantRequestType } from 'types/resolution';
import { isShipaidAdmin } from 'utils';
import { FeatureLandingRoute } from './NestedContainers/FeatureLanding/FeatureLandingRoute';
import { SwitchStore } from './NestedContainers/SwitchStore';

function LegacyRequestsList({ type }: { type: MerchantRequestType }) {
  const { role, user } = useUser();
  const location = useLocation();
  if (!canManageRequests(role, isShipaidAdmin(user)))
    return type === 'ISSUE' ? <Claims /> : <Returns />;
  const params = new URLSearchParams(location.search);
  params.set('type', type);
  if (!params.has('group')) params.set('group', 'ALL');
  return <Navigate replace to={`/requests?${params}`} />;
}

function LegacyOrderEditingPage({ page }: { page: 'requests' | 'activity' }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (page === 'activity') params.set('report', 'order-editing');
  else params.set('type', 'ORDER_EDITING');
  return (
    <Navigate
      replace
      to={{
        pathname: page === 'activity' ? '/requests/reports' : '/requests',
        search: params.toString() ? `?${params}` : '',
        hash:
          page === 'activity' && location.hash === '#order-edit-usage'
            ? location.hash
            : '',
      }}
    />
  );
}

function LegacyOrderEditingSettings() {
  const { hash } = useLocation();
  if (hash === '#order-edit-usage')
    return <LegacyOrderEditingPage page="activity" />;
  if (hash === '#order-edit-requests')
    return <LegacyOrderEditingPage page="requests" />;
  return <OrderSettings />;
}

export const MerchantApp: React.FC = withNotification(
  ({ showNotification }: any) => {
    const adminData = localStorage.getItem(
      process.env.REACT_APP_ADMIN_STORAGE_KEY || 'admin-context',
    );
    let adminContext: any;
    if (adminData) adminContext = JSON.parse(adminData);
    const userContext = useUser();
    const storeContext = useStore();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
      const saved = localStorage.getItem('sidebar-collapsed');
      return saved === 'true';
    });
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [isUpdateAppInstallModalOpen, setIsUpdateAppInstallModalOpen] =
      useState(false);
    const [updateStore] = useMutation(UPDATE_STORE, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    });

    const userStore = storeContext.userStores?.find(
      (userStore) => storeContext.storeId === userStore.storeId,
    );

    const completeStoreOnboarding = () => {
      updateStore({
        optimisticResponse: {
          updateStore: {
            id: userStore?.storeId,
            ...userStore,
            completedSteps: {
              ...userStore?.completedSteps,
              hasCompletedOnboarding: true,
            },
          },
        },
        variables: {
          storeId: storeContext.storeId,
          set: {
            completedSteps: {
              ...userStore?.completedSteps,
              hasCompletedOnboarding: true,
            },
          },
        },
      });
    };

    useEffect(() => {
      if (
        userStore &&
        !userStore?.completedSteps?.hasCompletedOnboarding &&
        storeContext?.storeProperties?.plan?.chargeConfirmed
      ) {
        setShowOnboarding(false);
      }
    }, [navigate, userStore, storeContext]);

    useEffect(() => {
      if (userStore?.completedSteps?.hasCompletedOnboarding && showOnboarding)
        setShowOnboarding(false);
    }, [userStore, showOnboarding]);

    const mainHeader = userContext?.announcement;

    const urlsWithoutYPadding = ['/', '/referral-program'];
    const isSettingsShellRoute =
      location.pathname.startsWith('/settings') ||
      location.pathname.startsWith('/order-settings');

    useEffect(() => {
      if (location.search.includes('charge_id')) {
        localStorage.setItem('waiting-payment', 'true');
      }
    }, [location.search]);

    const usingGodModeAccess = useMemo(() => {
      return Boolean(adminContext?.admin?.user?.user?.id);
    }, [adminContext]);

    return (
      <>
        <div>
          {usingGodModeAccess && (
            <AnnouncementHeader className="md:ml-0 bg-red-500">
              <Button
                onClick={() => {
                  if (userContext?.setUser && adminContext?.admin?.user?.user) {
                    userContext.setUser({
                      ...adminContext.admin.user.user,
                      roles: [userRoles.SHIPAID_ADMIN],
                      defaultRole: userRoles.SHIPAID_ADMIN,
                    });
                  }
                  if (
                    storeContext?.updateUserStore &&
                    adminContext.admin.store
                  ) {
                    storeContext.updateUserStore(adminContext.admin.store);
                  }
                  localStorage.removeItem(
                    process.env.REACT_APP_ADMIN_STORAGE_KEY || 'admin-context',
                  );
                  let redirectUrl = 'merchants';
                  if (userContext?.user?.id) {
                    redirectUrl += `/${userContext.user.id}`;
                  }
                  navigate(redirectUrl);
                }}
                className="ml-auto text-black bg-white bg-gray-100 hover:bg-gray-200"
                size={SizeEnum.xs}
              >
                Exit God Mode
              </Button>
            </AnnouncementHeader>
          )}
          {isUpdateAppInstallModalOpen && (
            <UpdateAppInstallModal
              isOpen={isUpdateAppInstallModalOpen}
              onClose={() => setIsUpdateAppInstallModalOpen(false)}
            />
          )}
          <div className="flex">
            {!isSettingsShellRoute && (
              <SideBar
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                isCollapsed={isSidebarCollapsed}
                onCollapseChange={(collapsed) => {
                  setIsSidebarCollapsed(collapsed);
                  localStorage.setItem('sidebar-collapsed', String(collapsed));
                }}
              />
            )}
            <div className="w-full">
              <AppBar
                isSidebarCollapsed={isSidebarCollapsed}
                hasSidebar={!isSettingsShellRoute}
                canSwitchStore={!usingGodModeAccess}
              />
              {mainHeader?.text?.length ? (
                <AnnouncementHeader className="bg-indigo-700 text-white">
                  <ReactMarkdown
                    className="reactMarkdown"
                    rehypePlugins={[rehypeRaw]}
                  >
                    {mainHeader.text.toString()}
                  </ReactMarkdown>
                </AnnouncementHeader>
              ) : null}
            </div>
          </div>
          <div>
            <div
              className={classNames(
                'flex flex-col flex-1 relative transition-all duration-300 ease-in-out',
                isSettingsShellRoute
                  ? 'md:pl-0'
                  : isSidebarCollapsed
                  ? 'md:pl-16'
                  : 'md:pl-72',
              )}
              id="main-content-area"
            >
              {showOnboarding && (
                <OnboardingWizard
                  closeWizard={(shouldCompleteOnboarding: boolean) => {
                    setShowOnboarding(false);
                    shouldCompleteOnboarding && completeStoreOnboarding();
                  }}
                />
              )}
              {!isSettingsShellRoute && (
                <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-white">
                  <button
                    type="button"
                    className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                    onClick={() => setSidebarOpen(true)}
                  >
                    <span className="sr-only">Open sidebar</span>
                    <MenuIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
              )}
              <main className="flex-1">
                <StoreSelectorWrapper
                  ignoreStoreCheck={usingGodModeAccess}
                  addNewStores
                >
                  <div
                    className={`${
                      urlsWithoutYPadding.includes(location.pathname)
                        ? 'py-0'
                        : 'py-6'
                    }`}
                  >
                    <Routes>
                      {/* <Route
                      index
                      element={<Navigate to="/" replace={true} />}
                    /> */}
                      <Route path="/" element={<Home />} />
                      <Route
                        path="switch-store/:storeId"
                        element={
                          <SwitchStore canSwitchStore={!usingGodModeAccess} />
                        }
                      />
                      <Route
                        path="dashboard"
                        element={
                          <Navigate
                            replace
                            to={{
                              pathname: '/',
                              search: location.search,
                              hash: location.hash,
                            }}
                          />
                        }
                      />
                      <Route element={<RequestsWorkspace />}>
                        <Route path="requests" element={<MerchantRequests />} />
                        <Route
                          path="requests/inspections"
                          element={<ReturnInspectionQueue />}
                        />
                        <Route path="orders" element={<Orders />} />
                        <Route
                          path="requests/reports"
                          element={
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                              <RequestReports />
                            </div>
                          }
                        />
                        <Route
                          path="requests/billing"
                          element={
                            <Navigate
                              replace
                              to={{
                                pathname: '/requests/reports',
                                search: location.search,
                                hash: '#return-billing',
                              }}
                            />
                          }
                        />
                        <Route
                          path="requests/new-issue"
                          element={<NewClaim />}
                        />
                      </Route>
                      <Route
                        path="claims"
                        element={<LegacyRequestsList type="ISSUE" />}
                      />
                      <Route path="claims/new-claim" element={<NewClaim />} />
                      <Route path="claims/:claimId" element={<SingleClaim />} />
                      <Route
                        path="returns"
                        element={<LegacyRequestsList type="RETURN" />}
                      />
                      <Route
                        path="returns/:returnId"
                        element={<ReturnDetail />}
                      />
                      <Route path="orders/:orderId" element={<ViewOrder />} />
                      <Route
                        path="tracking/:trackingNumber"
                        element={<TrackingDetail />}
                      />
                      <Route
                        path="features/:featureId"
                        element={<FeatureLandingRoute />}
                      />
                      {[
                        'order-editing',
                        'order-editing/requests',
                        'requests/order-editing',
                        'order-settings/requests',
                        'settings/order-settings/requests',
                      ].map((path) => (
                        <Route
                          key={path}
                          path={path}
                          element={<LegacyOrderEditingPage page="requests" />}
                        />
                      ))}
                      {[
                        'order-editing/activity',
                        'order-settings/activity',
                        'settings/order-settings/activity',
                      ].map((path) => (
                        <Route
                          key={path}
                          path={path}
                          element={<LegacyOrderEditingPage page="activity" />}
                        />
                      ))}
                      <Route
                        path="settings/order-settings/*"
                        element={<LegacyOrderEditingSettings />}
                      />
                      <Route path="settings/*" element={<Settings />} />
                      <Route
                        path="order-settings/*"
                        element={<LegacyOrderEditingSettings />}
                      />
                      <Route
                        path="referral-program"
                        element={<ReferralProgram />}
                      />
                      <Route path="settings" element={<Settings />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </div>
                </StoreSelectorWrapper>
              </main>
            </div>
          </div>
        </div>
      </>
    );
  },
);
